import { Link, Navigate, useParams } from "react-router-dom";
import { ArrowUpRight, ChevronRight } from "lucide-react";
import SiteLayout from "@/components/site/SiteLayout";
import JsonLd from "@/components/site/JsonLd";
import { getEpcTown, EPC_TOWNS, EPC_PRICING } from "@/lib/epc";
import { BRAND } from "@/lib/content";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

export default function EpcTown() {
  const { slug } = useParams();
  const town = getEpcTown(slug);
  if (!town) return <Navigate to="/epc" replace />;

  const others = EPC_TOWNS.filter((t) => t.slug !== slug);

  const schema = {
    "@context": "https://schema.org",
    "@graph": [
      {
        "@type": "Service",
        name: `EPC in ${town.name}`,
        serviceType: "Energy Performance Certificate",
        provider: {
          "@type": "ProfessionalService",
          name: "Declan Bishop — Property Consultant",
          url: "https://dbpropertyconsultant.co.uk",
          telephone: "+44 7947 981 727",
        },
        areaServed: { "@type": "City", name: town.name },
        offers: {
          "@type": "Offer",
          price: "85",
          priceCurrency: "GBP",
          description: `EPCs in ${town.name} from ${EPC_PRICING.from}. ${EPC_PRICING.note}`,
        },
        url: `https://dbpropertyconsultant.co.uk/epc/${town.slug}`,
      },
      {
        "@type": "FAQPage",
        mainEntity: town.faqs.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      },
      {
        "@type": "BreadcrumbList",
        itemListElement: [
          { "@type": "ListItem", position: 1, name: "Home", item: "https://dbpropertyconsultant.co.uk/" },
          { "@type": "ListItem", position: 2, name: "EPCs", item: "https://dbpropertyconsultant.co.uk/epc" },
          { "@type": "ListItem", position: 3, name: town.name, item: `https://dbpropertyconsultant.co.uk/epc/${town.slug}` },
        ],
      },
    ],
  };

  return (
    <SiteLayout
      pageTitle={`EPC ${town.name} — from £85, Accredited Assessor`}
      pageDescription={town.metaDescription}
      pagePath={`/epc/${town.slug}`}
    >
      <JsonLd id={`epc-town-schema-${town.slug}`} data={schema} />

      <section data-testid="epc-town-hero" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow pt-14 pb-16 md:pt-20 md:pb-20">
          <nav data-testid="epc-town-breadcrumb" className="flex items-center gap-1.5 text-[11px] tracking-[0.18em] uppercase text-brand-navy/55">
            <Link to="/" className="hover:text-brand-navy">Home</Link>
            <ChevronRight size={12} />
            <Link to="/epc" className="hover:text-brand-navy">EPCs</Link>
            <ChevronRight size={12} />
            <span className="text-brand-navy">{town.name}</span>
          </nav>
          <div className="overline mt-10">Domestic EPCs · {town.name}</div>
          <h1
            data-testid="epc-town-headline"
            className="mt-6 font-serif text-[42px] sm:text-6xl lg:text-7xl text-brand-navy leading-[0.98] tracking-tight max-w-4xl"
          >
            EPC in {town.name} — from{" "}
            <span className="italic font-light text-brand-champagne">{EPC_PRICING.from}.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-brand-navy/70 leading-relaxed">{town.intro}</p>
          <p data-testid="epc-town-pricing-note" className="mt-5 max-w-2xl text-sm text-brand-navy/60 leading-relaxed">
            {EPC_PRICING.note}
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link
              to="/contact"
              data-testid="epc-town-cta-primary"
              className="inline-flex items-center gap-3 bg-brand-navy text-brand-cream px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy-soft transition-colors"
            >
              Book an EPC in {town.name}
              <ArrowUpRight size={16} />
            </Link>
            <a
              href={`tel:${BRAND.phone.replace(/\s+/g, "")}`}
              data-testid="epc-town-cta-call"
              className="inline-flex items-center gap-3 border border-brand-navy/30 text-brand-navy px-7 py-4 text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-brand-navy hover:text-brand-cream transition-colors"
            >
              Call {BRAND.phone}
            </a>
          </div>
        </div>
      </section>

      <section data-testid="epc-town-content" className="bg-white border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="grid grid-cols-12 gap-y-10 md:gap-x-12">
            <div className="col-span-12 md:col-span-4">
              <div className="overline">Local knowledge</div>
              <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy leading-tight tracking-tight">
                EPCs in {town.name}, done properly.
              </h2>
              {town.areaSlug && (
                <p className="mt-6 text-sm text-brand-navy/60 leading-relaxed">
                  Need more than an EPC?{" "}
                  <Link to={`/areas/${town.areaSlug}`} className="ink-link font-medium text-brand-navy" data-testid="epc-town-area-link">
                    All services in {town.name} →
                  </Link>
                </p>
              )}
            </div>
            <div className="col-span-12 md:col-span-7 md:col-start-6 space-y-6 text-base md:text-lg text-brand-navy/75 leading-relaxed">
              {town.paragraphs.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section data-testid="epc-town-faq" className="bg-brand-cream border-b border-brand-navy/10">
        <div className="container-narrow py-20 md:py-28">
          <div className="overline">EPC questions in {town.name}</div>
          <h2 className="mt-6 font-serif text-3xl md:text-4xl text-brand-navy tracking-tight">
            Frequently asked locally.
          </h2>
          <div className="mt-10 max-w-3xl">
            <Accordion type="single" collapsible className="w-full" data-testid="epc-town-faq-accordion">
              {town.faqs.map((f, i) => (
                <AccordionItem key={i} value={`faq-${i}`} className="border-brand-navy/10">
                  <AccordionTrigger
                    data-testid={`epc-town-faq-question-${i}`}
                    className="text-left font-serif text-lg md:text-xl text-brand-navy hover:no-underline hover:text-brand-champagne py-6"
                  >
                    {f.q}
                  </AccordionTrigger>
                  <AccordionContent className="text-sm md:text-base text-brand-navy/75 leading-relaxed pb-6">
                    {f.a}
                  </AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </div>
        </div>
      </section>

      <section data-testid="epc-town-others" className="bg-brand-navy text-brand-cream">
        <div className="container-narrow py-16 md:py-20 flex flex-col md:flex-row md:items-center justify-between gap-8">
          <div>
            <div className="text-[11px] tracking-[0.28em] uppercase text-brand-champagne font-semibold">
              EPCs also available in
            </div>
            <div className="mt-4 flex flex-wrap gap-x-8 gap-y-3">
              {others.map((t) => (
                <Link
                  key={t.slug}
                  to={`/epc/${t.slug}`}
                  data-testid={`epc-town-other-${t.slug}`}
                  className="font-serif text-2xl md:text-3xl hover:text-brand-champagne transition-colors"
                >
                  {t.name}
                </Link>
              ))}
            </div>
          </div>
          <Link
            to="/epc"
            data-testid="epc-town-hub-link"
            className="inline-flex items-center gap-2 text-[12px] tracking-[0.22em] uppercase font-semibold text-brand-champagne hover:text-brand-cream transition-colors whitespace-nowrap"
          >
            All EPC areas
            <ArrowUpRight size={14} />
          </Link>
        </div>
      </section>
    </SiteLayout>
  );
}
